import React, { useState, useEffect } from 'react';
import { Menu, X, Github, Linkedin, Mail } from 'lucide-react';
import { navLinks, profileData } from '../data/mock';
import { Button } from './ui/button';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        isScrolled
          ? 'bg-[#0a0a0b]/80 backdrop-blur-xl border-b border-white/5'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a
            href="#"
            className="text-2xl font-light tracking-tight text-white hover:text-gray-300 transition-colors duration-500"
            style={{ fontFamily: 'Figtree, sans-serif' }}
          >
            {profileData.name.split(' ')[0]}
            <span className="text-indigo-500">.</span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link, index) => (
              <button
                key={link.name}
                onClick={() => scrollToSection(link.href)}
                className="relative text-sm text-gray-400 hover:text-white transition-all duration-500 hover:-translate-y-0.5 group"
                style={{
                  fontFamily: 'Figtree, sans-serif',
                  animationDelay: `${index * 100}ms`
                }}
              >
                {link.name}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-gradient-to-r from-indigo-500 to-purple-500 group-hover:w-full transition-all duration-500" />
              </button>
            ))}
          </div>

          {/* Social Icons & Contact Button */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href={profileData.github}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-gray-500 hover:text-white transition-all duration-500 hover:-translate-y-0.5 hover:drop-shadow-[0_0_8px_rgba(99,102,241,0.5)]"
            >
              <Github size={20} />
            </a>
            <a
              href={profileData.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-gray-500 hover:text-white transition-all duration-500 hover:-translate-y-0.5 hover:drop-shadow-[0_0_8px_rgba(99,102,241,0.5)]"
            >
              <Linkedin size={20} />
            </a>
            <Button
              onClick={() => scrollToSection('#contact')}
              className="ml-2 bg-white text-black hover:bg-gray-200 transition-all duration-500 hover:scale-105 hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]"
            >
              Contact
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-gray-400 hover:text-white transition-colors duration-300"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden absolute top-full left-0 right-0 bg-[#0a0a0b]/95 backdrop-blur-xl border-b border-white/5 transition-all duration-500 overflow-hidden ${
          isMobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 py-4 space-y-3">
          {navLinks.map((link) => (
            <button
              key={link.name}
              onClick={() => scrollToSection(link.href)}
              className="block w-full text-left py-2 text-gray-400 hover:text-white transition-colors duration-300"
            >
              {link.name}
            </button>
          ))}
          <div className="flex items-center gap-4 pt-4 border-t border-white/10">
            <a href={profileData.github} target="_blank" rel="noopener noreferrer">
              <Github size={20} className="text-gray-500 hover:text-white transition-colors" />
            </a>
            <a href={profileData.linkedin} target="_blank" rel="noopener noreferrer">
              <Linkedin size={20} className="text-gray-500 hover:text-white transition-colors" />
            </a>
            <a href={`mailto:${profileData.email}`}>
              <Mail size={20} className="text-gray-500 hover:text-white transition-colors" />
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
